import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, StatusBadge } from "@/components/StatusBits";
import { ContactDialog } from "@/components/contacts/ContactDialog";
import { ImportWizard } from "@/components/contacts/ImportWizard";
import { useApp } from "@/context/AppContext";
import { api, errMsg, fmtDT } from "@/lib/api";

export default function Contacts() {
  const { isAdmin } = useApp();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [imp, setImp] = useState(false);
  const load = useCallback(() => api.get("/contacts", { params: { q } }).then((r) => setRows(r.data)).catch(() => {}), [q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try {
      await api.delete(`/contacts/${c.id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="max-w-7xl p-6 space-y-5">
      <PageHeader title="Contacts" sub="Manual entry and CSV import. Imported contacts are never called automatically.">
        <Button variant="outline" data-testid="contacts-import-btn" onClick={() => setImp(true)}><Upload className="h-4 w-4 mr-1" />Import CSV</Button>
        <Button data-testid="contacts-add-btn" onClick={() => { setEdit(null); setOpen(true); }}><Plus className="h-4 w-4 mr-1" />Add contact</Button>
      </PageHeader>
      <Input data-testid="contacts-search-input" className="max-w-sm bg-white" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Business</TableHead><TableHead>Phone</TableHead><TableHead>Status</TableHead>
              <TableHead>Tags</TableHead><TableHead>Attempts</TableHead><TableHead>Callback</TableHead><TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} className="text-sm" data-testid={`contact-row-${c.id}`}>
                <TableCell className="py-2 font-medium">{c.name}</TableCell>
                <TableCell className="py-2 text-slate-600">{c.business}</TableCell>
                <TableCell className="py-2 font-mono text-xs">{c.phone}</TableCell>
                <TableCell className="py-2"><StatusBadge value={c.status} /></TableCell>
                <TableCell className="py-2 text-xs text-slate-500">{(c.tags || []).join(", ")}</TableCell>
                <TableCell className="py-2 text-xs">{c.attempts_total || 0}</TableCell>
                <TableCell className="py-2 text-xs">{c.callback_at ? fmtDT(c.callback_at) : "—"}</TableCell>
                <TableCell className="py-2">
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`contact-edit-${c.id}`} onClick={() => { setEdit(c); setOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                    {isAdmin && <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" data-testid={`contact-delete-${c.id}`} onClick={() => del(c)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8" data-testid="contacts-empty">No contacts yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <ContactDialog open={open} onOpenChange={setOpen} contact={edit} onSaved={load} />
      <ImportWizard open={imp} onOpenChange={setImp} onDone={load} />
    </div>
  );
}
